/**
 * Upload Schema — V2 Contracts (Unified V1 + V2 Creation)
 *
 * A single spreadsheet row creates both:
 *   1. A V1 contract (public.contract) via POST /v1/contracts
 *   2. A V2 contract link (public.contract) via POST /v2/contracts
 *
 * The customSubmit function handles the two-phase creation automatically.
 * Users see one flat set of fields — the V1/V2 split is invisible to them.
 */

import { UploadSchema } from '../types';

/**
 * Two-phase submit: create V1 contract first, then V2 link.
 */
async function submitContract({
	row,
	projectId,
	apiBaseUrl,
	headers,
	signal,
}: {
	row: Record<string, unknown>;
	projectId: number;
	apiBaseUrl: string;
	headers: Record<string, string>;
	signal?: AbortSignal;
}): Promise<{ id: number; name?: string }> {
	// ── Phase 1: Create V1 contract ──────────────────────────────────
	const v1Payload = {
		num: row.contractNum || row.description || `CONTRACT-${Date.now()}`,
		description: row.description || undefined,
		dateSigning: row.dateSigning || undefined,
		project: projectId,
		status: 'DRAFT',
		entities: row.entityId ? [Number(row.entityId)] : [],
		amounts: [
			{
				amount: Number(row.amount) || 0,
				currency: String(row.currency || 'USD'),
			},
		],
	};

	const v1Res = await fetch(`${apiBaseUrl}/v1/contracts`, {
		method: 'POST',
		headers,
		body: JSON.stringify(v1Payload),
		signal,
	});

	if (!v1Res.ok) {
		const errText = await v1Res.text().catch(() => `HTTP ${v1Res.status}`);
		let msg: string;
		try {
			const errJson = JSON.parse(errText);
			msg =
				errJson.error?.message ||
				errJson.message ||
				`V1 contract creation failed (${v1Res.status})`;
		} catch {
			msg = errText || `V1 contract creation failed (${v1Res.status})`;
		}
		throw new Error(msg);
	}

	const v1Data = await v1Res.json();
	if (v1Data.error) {
		throw new Error(v1Data.error.message || 'V1 contract creation failed');
	}

	const v1Contract = v1Data.data ?? v1Data;
	const contractId: number = v1Contract.id;
	const contractName: string = v1Contract.num ?? v1Contract.name ?? String(contractId);

	// ── Phase 2: Create V2 contract link ─────────────────────────────
	const contractAmount = Number(row.amount) || 0;
	const parsedCommitment =
		row.commitmentAmount !== undefined &&
		row.commitmentAmount !== null &&
		row.commitmentAmount !== ''
			? Number(row.commitmentAmount)
			: NaN;
	// Keep explicit commitment when valid and <= amount; otherwise default to current contract amount.
	const effectiveCommitmentAmount =
		Number.isFinite(parsedCommitment) && parsedCommitment > 0 && parsedCommitment <= contractAmount
			? parsedCommitment
			: contractAmount;

	const v2Payload = {
		contractId,
		activityId: Number(row.activityId),
		...(effectiveCommitmentAmount > 0 ? { commitmentAmount: effectiveCommitmentAmount } : {}),
		...(row.customData ? { customData: row.customData } : {}),
	};

	const v2Res = await fetch(`${apiBaseUrl}/v2/contracts`, {
		method: 'POST',
		headers,
		body: JSON.stringify(v2Payload),
		signal,
	});

	if (!v2Res.ok) {
		const errText = await v2Res.text().catch(() => `HTTP ${v2Res.status}`);
		let msg: string;
		try {
			const errJson = JSON.parse(errText);
			msg =
				errJson.error?.message || errJson.message || `V2 link creation failed (${v2Res.status})`;
		} catch {
			msg = errText || `V2 link creation failed (${v2Res.status})`;
		}
		throw new Error(
			`V1 contract "${contractName}" created (ID ${contractId}), but V2 link failed: ${msg}`
		);
	}

	const v2Data = await v2Res.json();
	if (v2Data.error) {
		throw new Error(
			`V1 contract "${contractName}" created (ID ${contractId}), but V2 link failed: ${v2Data.error.message}`
		);
	}

	const v2Link = v2Data.data ?? v2Data;
	return { id: v2Link.id, name: contractName };
}

export const v2ContractSchema: UploadSchema = {
	entityType: 'v2Contract',
	label: 'Contracts',
	apiEndpoint: '/v2/contracts',
	apiMethod: 'POST',
	customFieldEntityType: 'contract',
	customSubmit: submitContract,
	// Two rows in the same upload that share a contract number would create
	// duplicate V1 contracts (no unique constraint on public.contract.num).
	// Flag the duplicates at validate-preview time so the user can fix the
	// spreadsheet before anything is POSTed.
	duplicateKeyFields: ['contractNum'],
	fields: [
		// ── V2 Link Fields ───────────────────────────────────────────
		{
			name: 'activityId',
			label: 'Activity',
			dataType: 'relation',
			required: true,
			relation: {
				entityType: 'activity',
				matchField: 'name',
				idField: 'id',
				contextKey: 'activities',
			},
			headerAliases: ['Activity Name', 'Linked Activity'],
		},
		// ── V1 Contract Fields ───────────────────────────────────────
		{
			name: 'contractNum',
			label: 'Contract Number',
			dataType: 'string',
			required: true,
			headerAliases: ['Contract #', 'Contract No', 'Contract Number'],
		},
		{
			name: 'description',
			label: 'Contract Description',
			dataType: 'string',
			required: false,
			headerAliases: ['Contract Name', 'Description'],
		},
		{
			name: 'entityId',
			label: 'Supplier',
			dataType: 'relation',
			required: true,
			relation: {
				entityType: 'supplier',
				matchField: 'name',
				idField: 'id',
				contextKey: 'suppliers',
			},
			headerAliases: ['Supplier', 'Vendor', 'Entity', 'Supplier Name'],
		},
		{
			name: 'amount',
			label: 'Contract Amount',
			dataType: 'number',
			required: true,
			headerAliases: ['Amount', 'Value', 'Contract Value', 'Total Amount'],
		},
		{
			name: 'currency',
			label: 'Currency',
			dataType: 'string',
			required: false,
			defaultValue: 'USD',
			headerAliases: ['Currency Code', 'Ccy'],
		},
		{
			name: 'dateSigning',
			label: 'Signing Date',
			dataType: 'date',
			required: false,
			headerAliases: ['Date Signed', 'Signing Date', 'Date of Signing'],
		},
		{
			name: 'commitmentAmount',
			label: 'Commitment Amount',
			dataType: 'number',
			required: false,
			description:
				'Portion of contract amount committed to this activity (defaults to full amount)',
			headerAliases: ['Committed Amount', 'Commitment', 'Allocated Amount'],
		},
	],
};
